
import { defineComponent, ref } from "vue";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { swalErrNotification, swalNotification } from "@/core/helpers/utils";
import {
  IDanhMucVatTuResponseBase,
  IVatTuResponseBase,
} from "@/core/interfaces/ApiResponses";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { ButtonsType, ButtonTypeColors } from "@/core/enums/buttons.enums";
import { useLoading } from "@/core/hooks/use-loading";
import { usePagination } from "@/core/hooks/use-pagination";
import { VatTuService } from "@/core/services/VatTu.service";

import DanhMucVatTuService from "@/core/services/DanhMucVatTu.service";
import DanhSachVatTuTable from "@/views/crafted/pages/vat-tu/danh-sach-vat-tu-table/DanhSachVatTuTable.vue";
import ModalThemVatTu from "@/views/crafted/pages/danh-muc-vat-tu/modal-them-vat-tu/ModalThemVatTu.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import debounce from "lodash/debounce";
import ResponsiveLayout from "@/layout/_shared/ResponsiveLayout.vue";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import Can from "@/components/Can.vue";
import { DANH_MUC_VAT_TU } from "@/core/constants/permissions";
import DetailTopicLottie from "@/components/shared/detail-topic-lottie/DetailTopicLottie.vue";

export default defineComponent({
  name: "chi-tiet-danh-muc-vat-tu",

  components: {
    BaseLink,
    ButtonCustom,
    Can,
    DetailTopicLottie,
    DanhSachVatTuTable,
    LabelCustom,
    ModalThemVatTu,
    PageContentBase,
    PageContentSection,
    Pagination,
    ResponsiveLayout,
  },

  setup() {
    const { goBack, push, id } = useRouterCustom();

    const { moment } = useDateTime();

    const { startLoading, endLoading } = useLoading();

    const { currentPage, totalItems, perPage, perPageArr, searchTerm } =
      usePagination();

    const danhMucVatTu = ref<IDanhMucVatTuResponseBase | null>(null);

    const danhMucVatTuCha = ref<IDanhMucVatTuResponseBase | null>(null);

    return {
      DANH_MUC_VAT_TU,
      push,
      danhMucVatTuId: id,
      danhMucVatTu,
      danhMucVatTuCha,
      goBack,
      moment,
      startLoading,
      endLoading,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      searchTerm,
    };
  },

  data() {
    return {
      isModalOpen: false,
      ButtonsType,
      ButtonTypeColors,
      vatTuItems: [] as IVatTuResponseBase[],
      allVatTuItems: [] as IVatTuResponseBase[],
    };
  },

  async mounted() {
    setBreadcrumbLinks([
      {
        text: "Nhóm vật tư",
        routeData: {
          name: "danh-sach-danh-muc-vat-tu",
        },
      },
      {
        text: "Chi tiết",
      },
    ]);

    await this.fetchAllVatTuItems();

    await this.fetchData();

    setCurrentPageTitle(
      `${this.danhMucVatTu?.ten_danh_muc} (${this.danhMucVatTu?.ma_danh_muc})` ||
        ""
    );
  },

  computed: {
    totalVatTuDisplay(): string {
      if (this.danhMucVatTu && this.totalItems === 0) {
        return "Vật tư";
      }
      const totalVatTu = this.totalItems;
      if (this.danhMucVatTu) {
        return `Vật tư (${totalVatTu} loại vật tư)`;
      }
      return "";
    },
  },

  watch: {
    async currentPage() {
      await this.fetchVatTuItems();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchVatTuItems();

        return;
      }

      this.currentPage = 1;
    },

    danhMucVatTu: {
      handler() {
        this.fetchVatTuItems();
      },
      deep: true,
    },
  },

  methods: {
    async fetchData() {
      try {
        this.startLoading();

        const {
          data: { data },
        } = await DanhMucVatTuService.get(this.danhMucVatTuId);

        this.endLoading();

        this.danhMucVatTu = data;

        if (data.id_cha) {
          const {
            data: { data: parentData },
          } = await DanhMucVatTuService.get(data.id_cha);

          this.danhMucVatTuCha = parentData;
        }

        document.title = `${this.danhMucVatTu?.ten_danh_muc} (${this.danhMucVatTu?.ma_danh_muc}) - Nhóm vật tư - Đường sắt Hà Nội`;
      } catch (e) {
        this.endLoading();
        await swalNotification(
          "Có lỗi xảy ra khi lấy thông tin vật tư",
          "error"
        );
      }
    },

    async fetchAllVatTuItems() {
      if (!this.danhMucVatTuId) {
        this.allVatTuItems = [];

        return;
      }

      const {
        data: {
          data: { data },
        },
      } = await VatTuService.list({
        current_page: 1,
        per_page: 10000,
        id_dm_vat_tu: this.danhMucVatTuId,
      });

      this.allVatTuItems = data;
    },

    async fetchVatTuItems() {
      try {
        this.startLoading();

        if (!this.danhMucVatTuId) {
          this.vatTuItems = [];
          this.endLoading();
          return;
        }

        const {
          data: {
            data: { data, total },
          },
        } = await VatTuService.list({
          current_page: this.currentPage,
          per_page: this.perPage,
          id_dm_vat_tu: this.danhMucVatTuId,
          s: this.searchTerm,
        });

        this.vatTuItems = data;

        this.totalItems = total;

        this.endLoading();
      } catch (e) {
        this.endLoading();

        await swalErrNotification(e, "Lỗi khi lấy thông tin vật tư");
      }
    },

    openModal() {
      this.isModalOpen = true;
    },

    async onAddedVatTuItems() {
      this.isModalOpen = false;
      await this.fetchAllVatTuItems();
      await this.fetchData();
    },

    search: debounce(function () {
      this.fetchVatTuItems();
    }, 500),
  },
});
